import { useCallback, useEffect, useRef } from 'react';

import { type FilterKey, GroupCard, type GroupConfirmKind, type GroupSubscriptionData } from '@shared/dream-components';

import { type SeatsQuery, useGroupSeats } from './useGroupSeats';

/**
 * Mounts `useGroupSeats` per group and renders the controlled `<GroupCard>`.
 * Mounted via `GroupSubscriptionsElement.renderCard` so accordion state stays
 * in the element while each card runs its own fetcher.
 */
export type GroupSubscriptionCardContainerProps = {
  group: GroupSubscriptionData;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onOpenManageSeats: () => void;
  onOpenInviteByEmail: () => void;
  onOpenBulkInviteCsv: () => void;
  onOpenConfirm: (kind: GroupConfirmKind, seatIds: string[], firstEmail?: string | null) => void;
  onOpenModifyReduction: () => void;
  onOpenCancelReduction: () => void;
  onRetryFailedPayment?: () => void;
  onCancelSubscription?: () => void;
  /** "Keep subscription" — clears a scheduled cancellation on an active-but-
   *  canceling group. Direct-fire (no confirm). */
  onRestoreSubscription?: () => void;
  /** Action URL the past-due CTA posts to (native form → host mints a fresh
   *  Stripe portal session and redirects). */
  updatePaymentMethodAction?: string;
  onViewBilling?: () => void;
  /** Bumped by the parent after any mutation; triggers a per-card refetch. */
  mutationVersion: number;
  translations?: Record<string, string>;
};

export function GroupSubscriptionCardContainer({
  group,
  isExpanded,
  onToggleExpanded,
  onOpenManageSeats,
  onOpenInviteByEmail,
  onOpenBulkInviteCsv,
  onOpenConfirm,
  onOpenModifyReduction,
  onOpenCancelReduction,
  onRetryFailedPayment,
  onCancelSubscription,
  onRestoreSubscription,
  updatePaymentMethodAction,
  onViewBilling,
  mutationVersion,
  translations,
}: GroupSubscriptionCardContainerProps) {
  // Collapsed cards only show counts (already in the groups loader response).
  const { seats, pagination, isLoading, query, setQuery, refetch } = useGroupSeats(group.id, {
    enabled: isExpanded,
  });

  // Skip the first render so mount doesn't double-fetch alongside useGroupSeats.
  const lastSeenVersion = useRef(mutationVersion);
  useEffect(() => {
    if (lastSeenVersion.current === mutationVersion) return;
    lastSeenVersion.current = mutationVersion;
    refetch();
  }, [mutationVersion, refetch]);

  const handleFilterChange = useCallback(
    (status: FilterKey) => setQuery((prev: SeatsQuery) => ({ ...prev, status, page: 1 })),
    [setQuery],
  );
  const handleSearchChange = useCallback(
    (q: string) => setQuery((prev: SeatsQuery) => ({ ...prev, q, page: 1 })),
    [setQuery],
  );
  const handlePageChange = useCallback(
    (page: number) => setQuery((prev: SeatsQuery) => ({ ...prev, page })),
    [setQuery],
  );

  return (
    <GroupCard
      group={group}
      isExpanded={isExpanded}
      onToggleExpanded={onToggleExpanded}
      seats={seats}
      pagination={pagination}
      isLoadingSeats={isLoading}
      activeFilter={query.status}
      searchQuery={query.q}
      onFilterChange={handleFilterChange}
      onSearchChange={handleSearchChange}
      onPageChange={handlePageChange}
      onOpenManageSeats={onOpenManageSeats}
      onOpenInviteByEmail={onOpenInviteByEmail}
      onOpenBulkInviteCsv={onOpenBulkInviteCsv}
      onOpenConfirm={onOpenConfirm}
      onModifyReduction={onOpenModifyReduction}
      onCancelReduction={onOpenCancelReduction}
      onRetryFailedPayment={onRetryFailedPayment}
      onCancelSubscription={onCancelSubscription}
      onRestoreSubscription={onRestoreSubscription}
      updatePaymentMethodAction={updatePaymentMethodAction}
      onViewBilling={onViewBilling}
      translations={translations}
    />
  );
}
